import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import API from "../../API";
import { formatDate, money } from "./MarketplaceBase";

/*
 * Contact-fee refund management. Operations reviews driver refund requests
 * (dead contact) from both marketplace services and approves or rejects
 * them. Cases without a measured call, or with a seemingly real
 * conversation, land here as "İncelemede".
 */

const FILTERS = [
  { label: "İncelemede", value: "under_review" },
  { label: "Onaylanan", value: "approved" },
  { label: "Reddedilen", value: "rejected" },
  { label: "Tümü", value: "all" },
];
const STATUS_CHIP = {
  under_review: { label: "İncelemede", bg: "#FEF3C7", fg: "#B45309" },
  approved: { label: "Onaylandı", bg: "#DCFCE7", fg: "#15803D" },
  rejected: { label: "Reddedildi", bg: "#FEE2E2", fg: "#B91C1C" },
};

export default function MarketplaceRefundsScreen({ history }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null); // { item, action }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    API.fetchClient("panel/getmarketplacerefunds", { statusFilter: FILTERS[filter].value }).then((result) => {
      setLoading(false);
      if (result?.items) setItems(result.items);
    });
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const decide = () => {
    if (!decision) return;
    setBusy(true);
    API.fetchClient("panel/decidemarketplacerefund", {
      serviceType: decision.item.serviceType,
      refundId: decision.item.refundId,
      decision: decision.action,
      note: note.trim() || undefined,
    }).then((result) => {
      setBusy(false);
      if (result?.status) { setDecision(null); setNote(""); load(); }
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, pb: 6, flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Pazaryeri İadeleri</Typography>
          <Typography variant="body2" color="text.secondary">İletişim ücreti iade talepleri — inceleme, onay ve red</Typography>
        </Box>
        <IconButton onClick={load}><RefreshIcon /></IconButton>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        {FILTERS.map((item, index) => (
          <Chip key={item.value} label={item.label} color={index === filter ? "primary" : "default"} variant={index === filter ? "filled" : "outlined"} onClick={() => setFilter(index)} />
        ))}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          {loading && !items ? <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#F7FAFC" }}>
                <TableRow>
                  <TableCell>Sürücü</TableCell>
                  <TableCell>Hizmet</TableCell>
                  <TableCell>Talep ID</TableCell>
                  <TableCell>Tutar</TableCell>
                  <TableCell>Konuşma</TableCell>
                  <TableCell>Tahsil</TableCell>
                  <TableCell>Talep</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(items || []).map((item) => {
                  const chip = STATUS_CHIP[item.status] || STATUS_CHIP.under_review;
                  return (
                    <TableRow hover key={item.refundId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ cursor: item.driverId ? "pointer" : "default" }} onClick={() => item.driverId && history.push(`/driver/${item.driverId}`)}>{item.driverName}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.driverPhone}</Typography>
                      </TableCell>
                      <TableCell>{item.serviceType === "intercity" ? "Şehirlerarası" : "Evden Eve"}</TableCell>
                      <TableCell>{item.requestId ? <a href={`/${item.serviceType === "intercity" ? "intercity" : "home-moving"}/${item.requestId}`} target="_blank" rel="noopener noreferrer">#{item.requestId.slice(-8).toUpperCase()}</a> : "-"}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={800}>{item.amount != null ? money(item.amount) : "-"}</Typography></TableCell>
                      <TableCell>{item.totalCallSeconds || 0} sn</TableCell>
                      <TableCell>{item.feeCapturedAt ? formatDate(item.feeCapturedAt) : "-"}</TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell>
                        <span style={{ backgroundColor: chip.bg, color: chip.fg, padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{chip.label}</span>
                        {item.note ? <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.4, maxWidth: 220 }} noWrap>{item.note}</Typography> : null}
                      </TableCell>
                      <TableCell align="right">
                        {item.status === "under_review" ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="contained" color="success" onClick={() => { setDecision({ item, action: "approve" }); setNote(""); }}>Onayla</Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => { setDecision({ item, action: "reject" }); setNote(""); }}>Reddet</Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">{item.decidedAt ? formatDate(item.decidedAt) : "-"}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {items && items.length === 0 ? (
                  <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Bu filtrede iade talebi yok.</Typography></TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>

      <Dialog open={!!decision} onClose={() => setDecision(null)} fullWidth maxWidth="xs">
        <DialogTitle>{decision?.action === "approve" ? "İadeyi Onayla" : "İadeyi Reddet"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {decision?.item?.driverName} · {decision?.item?.amount != null ? money(decision.item.amount) : "-"} · {decision?.item?.totalCallSeconds || 0} sn konuşma
          </Typography>
          <TextField fullWidth multiline minRows={2} label="Not (isteğe bağlı)" value={note} onChange={(event) => setNote(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)}>Vazgeç</Button>
          <Button variant="contained" color={decision?.action === "approve" ? "success" : "error"} disabled={busy} onClick={decide}>
            {decision?.action === "approve" ? "Onayla ve İade Et" : "Reddet"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
